import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, X, Save } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ENTITY_SCHEMAS } from './entitySchemas';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const csvToArr = (val) => (typeof val === 'string' ? val.split(',').map((s) => s.trim()).filter(Boolean) : val || []);
const arrToCsv = (val) => (Array.isArray(val) ? val.join(', ') : val ?? '');

const AdminEntity = () => {
  const { entity } = useParams();
  const schema = ENTITY_SCHEMAS[entity];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=closed, {} = creating, item = editing
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!schema) return;
    setLoading(true);
    api
      .get(`/services/${entity}`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error('Failed to load items'))
      .finally(() => setLoading(false));
  }, [entity, schema]);

  if (!schema) return <Navigate to="/admin" replace />;

  const openCreate = () => {
    const initial = {};
    schema.fields.forEach((f) => { initial[f.name] = f.type === 'csv' ? '' : ''; });
    setEditing(initial);
  };

  const openEdit = (item) => {
    const data = { ...item };
    schema.fields.forEach((f) => {
      if (f.type === 'csv') data[f.name] = arrToCsv(data[f.name]);
    });
    setEditing(data);
  };

  const closeEdit = () => setEditing(null);

  const handleChange = (name, value) => {
    setEditing((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing };
      schema.fields.forEach((f) => {
        if (f.type === 'csv') payload[f.name] = csvToArr(payload[f.name]);
        if (f.type === 'number' && payload[f.name] !== '' && payload[f.name] != null) {
          payload[f.name] = Number(payload[f.name]);
        }
      });
      // Remove empty id on create so backend auto-generates
      if (!payload.id) delete payload.id;

      const isEdit = items.some((i) => i.id === payload.id);
      if (isEdit) {
        const { id, ...body } = payload;
        const res = await api.put(`/admin/services/${entity}/${id}`, body);
        setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
        toast.success(`${schema.label.replace(/s$/, '')} updated`);
      } else {
        const res = await api.post(`/admin/services/${entity}`, payload);
        setItems((prev) => [res.data, ...prev]);
        toast.success(`${schema.label.replace(/s$/, '')} created`);
      }
      closeEdit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/services/${entity}/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success('Deleted successfully');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid={`admin-${entity}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{schema.label}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{items.length} item(s)</p>
        </div>
        <Button onClick={openCreate} data-testid={`add-${entity}-btn`} className="bg-red-600 hover:bg-red-700">
          <Plus className="h-4 w-4 mr-1.5" />
          Add {schema.label.replace(/s$/, '')}
        </Button>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">No items yet. Click "Add" to create one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {schema.listFields.map((f) => (
                    <th key={f} className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">{f}</th>
                  ))}
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map((item) => (
                  <tr key={item.id} data-testid={`row-${item.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    {schema.listFields.map((f) => (
                      <td key={f} className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-xs truncate">{String(item[f] ?? '—')}</td>
                    ))}
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                      <button
                        onClick={() => openEdit(item)}
                        data-testid={`edit-${item.id}`}
                        className="p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={deleting === item.id}
                        data-testid={`delete-${item.id}`}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400 disabled:opacity-50"
                      >
                        {deleting === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={closeEdit}>
          <div
            className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-modal"
          >
            <div className="sticky top-0 bg-white dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing.id ? `Edit ${schema.label.replace(/s$/, '')}` : `New ${schema.label.replace(/s$/, '')}`}
              </h3>
              <button onClick={closeEdit} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" data-testid="modal-close-btn">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {schema.fields.map((f) => (
                <div key={f.name} className="space-y-1">
                  <Label htmlFor={f.name} className="text-gray-700 dark:text-gray-300 text-sm">
                    {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  {f.type === 'textarea' ? (
                    <Textarea
                      id={f.name}
                      value={editing[f.name] ?? ''}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                      placeholder={f.placeholder}
                      rows={3}
                      data-testid={`field-${f.name}`}
                    />
                  ) : (
                    <Input
                      id={f.name}
                      type={f.type === 'number' ? 'number' : 'text'}
                      step={f.step}
                      value={editing[f.name] ?? ''}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                      required={f.required}
                      placeholder={f.placeholder}
                      data-testid={`field-${f.name}`}
                    />
                  )}
                  {f.type === 'image' && editing[f.name] && (
                    <img src={editing[f.name]} alt="" className="mt-2 h-20 rounded object-cover" />
                  )}
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
                <Button type="button" variant="outline" onClick={closeEdit}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="modal-save-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Save</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEntity;
